/**
 * CherryScoreDisplay Komponente
 *
 * CherryScore als Sterne (0-5)
 */

import React from 'react';
import { Star } from 'lucide-react';
import { getStarClasses, getStarLevel } from '@/config/ui.config';
import { cn } from '@/lib/utils';

interface CherryScoreDisplayProps {
  /** CherryScore (0-5 Sterne) */
  cherryScore: number | null;
  /** Größe */
  size?: 'sm' | 'md' | 'lg';
  /** Labels anzeigen */
  showLabels?: boolean;
  /** Layout */
  layout?: 'horizontal' | 'vertical';
}

const sizeStyles = {
  sm: {
    container: 'gap-1',
    star: 'w-4 h-4',
    value: 'text-lg',
    label: 'text-xs',
  },
  md: {
    container: 'gap-1.5',
    star: 'w-5 h-5',
    value: 'text-xl',
    label: 'text-sm',
  },
  lg: {
    container: 'gap-2',
    star: 'w-6 h-6',
    value: 'text-2xl',
    label: 'text-base',
  },
};

export function CherryScoreDisplay({
  cherryScore,
  size = 'md',
  showLabels = true,
  layout = 'horizontal',
}: CherryScoreDisplayProps): React.ReactElement {
  const styles = sizeStyles[size];
  const starStyles = cherryScore !== null ? getStarClasses(cherryScore) : getStarClasses(0);
  const level = cherryScore !== null ? getStarLevel(cherryScore) : 'unrated';

  const containerClass =
    layout === 'horizontal' ? 'flex items-center gap-4' : 'flex flex-col gap-3';

  const filledStars = cherryScore !== null ? Math.round(cherryScore) : 0;
  const displayValue = cherryScore !== null ? cherryScore.toFixed(1) : '–';

  const levelLabel =
    level === 'excellent'
      ? 'Sehr überzeugend'
      : level === 'good'
        ? 'Überzeugend'
        : level === 'medium'
          ? 'Durchschnitt'
          : level === 'poor'
            ? 'Nicht überzeugend'
            : 'Nicht bewertbar';

  return (
    <div className={containerClass}>
      {/* CherryScore als Sterne */}
      <div className="flex items-center gap-2">
        <div className={cn('flex flex-col items-center', starStyles.bg, 'rounded-xl p-3')}>
          {/* Sterne */}
          <div className={cn('flex items-center', styles.container)}>
            {[0, 1, 2, 3, 4].map((i) => (
              <Star
                key={i}
                className={cn(
                  styles.star,
                  i < filledStars ? [starStyles.starColor, 'fill-current'] : 'text-gray-300'
                )}
              />
            ))}
          </div>
          {/* Numerischer Wert */}
          <span className={cn(styles.value, 'font-bold mt-1', starStyles.text)}>
            {displayValue}
          </span>
        </div>

        {showLabels && (
          <div className="flex flex-col">
            <span className={cn('font-semibold text-charcoal-900', styles.label)}>
              CherryScore
            </span>
            <span className={cn('text-charcoal-500', styles.label)}>{levelLabel}</span>
          </div>
        )}
      </div>

    </div>
  );
}

/**
 * Kompakte Version nur mit CherryScore als Sterne-Badge
 */
export function CherryScoreBadge({
  score,
  size = 'md',
  showValue = true,
}: {
  score: number | null;
  size?: 'sm' | 'md' | 'lg';
  showValue?: boolean;
}): React.ReactElement {
  const styles = sizeStyles[size];
  const starStyles = score !== null ? getStarClasses(score) : getStarClasses(0);
  const filledStars = score !== null ? Math.round(score) : 0;

  return (
    <div
      className={cn('flex flex-col items-center rounded-xl p-2', starStyles.bg)}
      title={score !== null ? `CherryScore: ${score.toFixed(1)} Sterne` : 'Nicht bewertbar'}
    >
      <div className={cn('flex items-center', styles.container)}>
        {[0, 1, 2, 3, 4].map((i) => (
          <Star
            key={i}
            className={cn(
              styles.star,
              i < filledStars ? [starStyles.starColor, 'fill-current'] : 'text-gray-300'
            )}
          />
        ))}
      </div>
      {showValue && (
        <span className={cn('font-bold', styles.label, starStyles.text)}>
          {score !== null ? score.toFixed(1) : '–'}
        </span>
      )}
    </div>
  );
}

/**
 * Nur Sterne inline (ohne Badge-Rahmen)
 */
export function CherryScoreStars({
  score,
  size = 'md',
  showValue = false,
}: {
  score: number | null;
  size?: 'sm' | 'md' | 'lg';
  showValue?: boolean;
}): React.ReactElement {
  const styles = sizeStyles[size];
  const starStyles = score !== null ? getStarClasses(score) : getStarClasses(0);
  const filledStars = score !== null ? Math.round(score) : 0;

  return (
    <div className="flex items-center gap-1.5">
      <div className={cn('flex items-center', styles.container)}>
        {[0, 1, 2, 3, 4].map((i) => (
          <Star
            key={i}
            className={cn(
              styles.star,
              i < filledStars ? [starStyles.starColor, 'fill-current'] : 'text-gray-300'
            )}
          />
        ))}
      </div>
      {showValue && (
        <span className={cn('font-semibold', styles.label, starStyles.text)}>
          {score !== null ? score.toFixed(1) : '–'}
        </span>
      )}
    </div>
  );
}

export default CherryScoreDisplay;
